import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { SettingsTab } from './settingsAgentInstall'

/**
 * Settings -> AGENT SETTINGS -> Remote Connect, calea noua.
 *
 *   Companies::remote_connect      -> GET  /settings/remote_connect
 *   Remote_connect::update_company -> POST /settings/remote_connect
 *
 * ASTA E FILA SCREENCONNECT. RustDesk are ecranul lui
 * (`api/settingsRustdesk.ts`), in aceeasi sectiune a barei, cu comutatorul lui.
 */

export type RemoteConnectConfig = {
  /** firma.remote_connect_access */
  remote_connect_access: boolean
  /** it_company_settings.enable_technician_remote_control */
  enable_technician_remote_control: boolean
}

/** Un server RustDesk al furnizorului (`it_remote_connect_servers`). */
export type RustdeskServer = {
  id: number
  label: string
  hbbsHost: string
  hbbsPort: number
  hbbrHost: string
  hbbrPort: number
  /** Cheia PUBLICA a serverului (id_ed25519.pub), 44 de caractere. */
  serverKey: string
  active: boolean
  /** Randul vechi din Settings -> RustDesk; valorile se scriu acolo. */
  fromSettings: boolean
}

export type RustdeskInstallState = '' | 'not_installed' | 'downloading' | 'installing' | 'installed' | 'failed'

/** Starea RustDesk pe un aparat, din raportul agentului (POST /rustdesk/status). */
export type RustdeskDeviceState = {
  deviceId: number
  name: string
  rustdeskId: string
  installState: RustdeskInstallState
  installError: string
  requestedAt: string
  stateAt: string
  activeServer: string
  lastTamperAt: string
  lastTamperKeys: string
  tamperCount: number
}

export type RustdeskOrgSection =
  | { ready: false }
  | {
      ready: true
      installMode: 'start' | 'demand'
      installModeInherited: boolean
      whitelist: string[]
      whitelistInherited: boolean
      providerWhitelist: string[]
      servers: RustdeskServer[]
      /** Serverele firmei, in ordine; [] = toate serverele active ale furnizorului. */
      companyServerIds: number[]
      devices: RustdeskDeviceState[]
    }

export type RemoteConnectShell = {
  pageTitle: string
  moduleUrl: string
  tabs: SettingsTab[]
  /** `settings.my_company.edit`. In portal scrierea cerea doar `.read`. */
  canWrite: boolean
  settings: RemoteConnectConfig
  /** RustDesk pe organizatie; lipseste pe un api mai vechi. */
  rustdesk?: RustdeskOrgSection
}

export const remoteConnectShellQuery = queryOptions({
  queryKey: ['settings', 'remote_connect'],
  queryFn: () => apiGet<RemoteConnectShell>('/settings/remote_connect'),
})

export function saveRemoteConnect(fields: Record<string, PostValue>) {
  return apiPost<RemoteConnectShell & { saved: boolean }>('/settings/remote_connect', fields)
}

/*
 * RustDesk pe organizatie (`App\Settings\RemoteConnect\RustdeskOrg`). Toate
 * raspund cu ecranul intreg.
 */

export function saveRustdeskInstallMode(mode: 'start' | 'demand' | 'inherit') {
  return apiPost<RemoteConnectShell & { saved: boolean }>('/settings/remote_connect/rustdesk/install', { install_mode: mode })
}

export function saveRustdeskWhitelist(fields: { scope: 'company' | 'provider'; inherit?: boolean; whitelist?: string }) {
  const body: Record<string, PostValue> = { scope: fields.scope, whitelist: fields.whitelist ?? '' }
  if (fields.inherit) body.inherit = '1'
  return apiPost<RemoteConnectShell & { saved: boolean }>('/settings/remote_connect/rustdesk/whitelist', body)
}

export type RustdeskServerForm = {
  id?: number
  label: string
  hbbs_host: string
  hbbs_port: string
  hbbr_host: string
  hbbr_port: string
  server_key: string
  active: boolean
}

export function saveRustdeskServer(form: RustdeskServerForm) {
  const body: Record<string, PostValue> = {
    label: form.label,
    hbbs_host: form.hbbs_host,
    hbbs_port: form.hbbs_port,
    hbbr_host: form.hbbr_host,
    hbbr_port: form.hbbr_port,
    server_key: form.server_key,
    active: form.active ? '1' : '0',
  }
  if (form.id) body.id = form.id
  return apiPost<RemoteConnectShell & { saved: boolean }>('/settings/remote_connect/rustdesk/servers', body)
}

export function saveRustdeskCompanyServers(ids: number[]) {
  return apiPost<RemoteConnectShell & { saved: boolean }>('/settings/remote_connect/rustdesk/company_servers', {
    servers: ids.join(','),
  })
}
